const radiusOfTheEarthMeters = 6371e3

function getDistance({ lat1, lng1, lat2, lng2 }: { lat1: number; lng1: number; lat2: number; lng2: number }) {
  const R = 6371e3
  const lat1Radians = toRadians(lat1)
  const lat2Radians = toRadians(lat2)
  const lng1Radians = toRadians(lng1)
  const lng2Radians = toRadians(lng2)

  let longitudeDelta = lng2Radians - lng1Radians
  let latitudeDelta = lat2Radians - lat1Radians

  const delta = Math.log(Math.tan(Math.PI / 4 + lat2Radians / 2) / Math.tan(Math.PI / 4 + lat1Radians / 2))
  const q = Math.abs(delta) > 10e-12 ? latitudeDelta / delta : Math.cos(lat1Radians) // E-W course becomes ill-conditioned with 0/0

  // if dLon over 180° take shorter rhumb line across the anti-meridian:
  if (Math.abs(longitudeDelta) > Math.PI) {
    longitudeDelta = longitudeDelta > 0 ? -(2 * Math.PI - longitudeDelta) : 2 * Math.PI + longitudeDelta
  }

  const dist = Math.sqrt(latitudeDelta * latitudeDelta + q * q * longitudeDelta * longitudeDelta) * R
  return dist
}

function toRadians(angle: number) {
  let normalizedAngle = angle % 360
  normalizedAngle = normalizedAngle < 0 ? normalizedAngle + 360 : normalizedAngle
  return normalizedAngle * (Math.PI / 180)
}
function toDegrees(angle: number) {
  const result = (angle * (180 / Math.PI) + 360) % 360
  return result
}

function normalizeLongitude(longitude: number) {
  return ((longitude + 540) % 360) - 180
}

function normalizeLatitude(latitude: number) {
  if (latitude > 180) {
    return normalizeLatitude(latitude - 360)
  } else if (latitude < -180) {
    return normalizeLatitude(latitude + 360)
  } else if (latitude > 90) {
    return 90
  } else if (latitude < -90) {
    return -90
  } else {
    return latitude
  }
}

/**
 * Bearing is measured from North direction
 * i.e 0° bearing means North, 90° bearing is East, 180° bearing is measured to be South, and 270° to be West.
 */
function getBearing({ lat1, lat2, lng1, lng2 }: { lat1: number; lng1: number; lat2: number; lng2: number }) {
  const lat1Radians = toRadians(lat1)
  const lat2Radians = toRadians(lat2)
  const lng1Radians = toRadians(lng1)
  const lng2Radians = toRadians(lng2)

  const y = Math.sin(lng2Radians - lng1Radians) * Math.cos(lat2Radians)
  const x =
    Math.cos(lat1Radians) * Math.sin(lat2Radians) -
    Math.sin(lat1Radians) * Math.cos(lat2Radians) * Math.cos(lng2Radians - lng1Radians)
  return toDegrees(Math.atan2(y, x)) % 360
}

function followBearing({ lat, lng, bearing, distance }: { lat: number; lng: number; bearing: number; distance: number }) {
  const lat1Radians = toRadians(lat)
  const lng1Radians = toRadians(lng)
  const bearingRadians = toRadians(bearing)
  const direction = bearingRadians >= 0 && bearingRadians <= Math.PI ? 'north' : 'south'
  const maxAngularDistance = 6.28 // here to prevent going all the way around the earth
  const angularDistance = Math.min(distance / radiusOfTheEarthMeters, maxAngularDistance)

  const lat2 = Math.asin(
    Math.sin(lat1Radians) * Math.cos(angularDistance) +
      Math.cos(lat1Radians) * Math.sin(angularDistance) * Math.cos(bearingRadians)
  )
  const lon2 =
    lng1Radians +
    Math.atan2(
      Math.sin(bearingRadians) * Math.sin(angularDistance) * Math.cos(lat1Radians),
      Math.cos(angularDistance) - Math.sin(lat1Radians) * Math.sin(lat2)
    )

  const normalizeLat2 = normalizeLatitude(toDegrees(lat2))
  const lat3 = direction === 'north' ? (normalizeLat2 < lat ? 90 : normalizeLat2) : normalizeLat2 > lat ? -90 : normalizeLat2 // ensures we don't go around of the poles
  const output = { lat: lat3, lng: normalizeLongitude(toDegrees(lon2)) }
  return output
}

function getMidpoint(bounds: { lat1: number; lng1: number; lat2: number; lng2: number }) {
  const lat1Radians = toRadians(bounds.lat1)
  const lat2Radians = toRadians(bounds.lat2)
  let lng1Radians = toRadians(bounds.lng1)
  const lng2Radians = toRadians(bounds.lng2)
  if (Math.abs(lng2Radians - lng1Radians) > Math.PI) lng1Radians += 2 * Math.PI // crossing anti-meridian

  const lat3Radians = (lat1Radians + lat2Radians) / 2

  const f1 = Math.tan(Math.PI / 4 + lat1Radians / 2)
  const f2 = Math.tan(Math.PI / 4 + lat2Radians / 2)
  const f3 = Math.tan(Math.PI / 4 + lat3Radians / 2)

  let lng3Radians =
    ((lng2Radians - lng1Radians) * Math.log(f3) + lng1Radians * Math.log(f2) - lng2Radians * Math.log(f1)) /
    Math.log(f2 / f1)
  if (!isFinite(lng3Radians)) lng3Radians = (lng1Radians + lng2Radians) / 2 // parallel of latitude
  return {
    lat: normalizeLatitude(toDegrees(lat3Radians)),
    lng: normalizeLongitude(toDegrees(lng3Radians))
  }
}

/**
 * uses snake_case because this is stored in search param
 */
export interface LatLngBounds {
  ne_lat: number
  ne_lng: number
  sw_lat: number
  sw_lng: number
}

function getBoundsAroundCenter({
  centerLat,
  centerLng,
  innerRadius
}: {
  centerLat: number
  centerLng: number
  innerRadius: number
}) {
  const proposedOuterRadius = Math.sqrt(Math.pow(innerRadius, 2) + Math.pow(innerRadius, 2))
  const maxOuterRadius = (Math.PI / 2) * radiusOfTheEarthMeters /** 1/4 of earths's circumference */
  const outerRadius = Math.min(proposedOuterRadius, maxOuterRadius)
  const { lat: ne_lat, lng: ne_lng } = followBearing({
    lat: centerLat,
    lng: centerLng,
    bearing: 45,
    distance: outerRadius
  })

  const { lat: sw_lat, lng: sw_lng } = followBearing({
    lat: centerLat,
    lng: centerLng,
    bearing: 225,
    distance: outerRadius
  })

  return {
    ne_lat,
    ne_lng,
    sw_lat,
    sw_lng
  }
}

function addPaddingToBounds(
  bounds: LatLngBounds,
  options?: {
    padding?: number
  }
): LatLngBounds {
  const [lat1, lng1, lat2, lng2] = [bounds.ne_lat, bounds.ne_lng, bounds.sw_lat, bounds.sw_lng]
  const padding = options?.padding ? options.padding : 0
  if (padding === 0) {
    return bounds
  }
  const paddingMultiplier = padding / 100
  const distance = getDistance({ lat1, lng1, lat2, lng2 })
  const additionalDistance = ((distance || 1) * paddingMultiplier) / 2
  const bearingFromNEToSW = getBearing({ lat1, lng1, lat2, lng2 })
  const bearingFromSWToNE = (bearingFromNEToSW + 180) % 360

  const newNe = followBearing({
    lat: bounds.ne_lat,
    lng: bounds.ne_lng,
    distance: additionalDistance,
    bearing: bearingFromSWToNE
  })
  const newSw = followBearing({
    lat: bounds.sw_lat,
    lng: bounds.sw_lng,
    distance: additionalDistance,
    bearing: bearingFromNEToSW
  })

  return {
    ne_lat: newNe.lat,
    ne_lng: newNe.lng,
    sw_lat: newSw.lat,
    sw_lng: newSw.lng
  }
}

function isPointInBoundingBox(point: { lat: number; lng: number }, bounds: LatLngBounds) {
  const minLat = Math.min(bounds.ne_lat, bounds.sw_lat)
  const maxLat = Math.max(bounds.ne_lat, bounds.sw_lat)

  let lng = point.lng
  let ne_lng = bounds.ne_lng
  let sw_lng = bounds.sw_lng
  if (ne_lng < 0 && sw_lng > 0) sw_lng = sw_lng - 360
  if (ne_lng < 0 && lng > 0) lng = lng - 360

  const isInBounds = point.lat <= maxLat && point.lat >= minLat && lng <= ne_lng && lng >= sw_lng

  return isInBounds
}

/**
 * Snap a coordinate to the nearest 0.001° grid line, always expanding outward.
 * NE bounds ceil (push north/east), SW bounds floor (push south/west), so the
 * quantized bounding box always *contains* the raw viewport — no visible pin
 * is ever excluded from the DB query.
 *
 * The ±1e-9 epsilon absorbs IEEE 754 representation noise so that a value
 * already on the grid (e.g. 49.185 from a previous quantization) stays put
 * instead of being bumped to the next grid line.
 */
function ceilToGrid(n: number) {
  return Math.ceil(n * 1000 - 1e-9) / 1000
}
function floorToGrid(n: number) {
  return Math.floor(n * 1000 + 1e-9) / 1000
}

function quantizeBoundsExpanding(bounds: LatLngBounds): {
  ne_lat: string
  ne_lng: string
  sw_lat: string
  sw_lng: string
} {
  return {
    ne_lat: ceilToGrid(bounds.ne_lat).toFixed(3),
    ne_lng: ceilToGrid(bounds.ne_lng).toFixed(3),
    sw_lat: floorToGrid(bounds.sw_lat).toFixed(3),
    sw_lng: floorToGrid(bounds.sw_lng).toFixed(3)
  }
}

export const LatLngUtils = {
  followBearing,
  getDistance,
  getBearing,
  getMidpoint,
  addPaddingToBounds,
  getBoundsAroundCenter,
  isPointInBoundingBox,
  quantizeBoundsExpanding
}
